import React, { Component } from "react";
import { connect } from "react-redux";
import EmployeeMergeService from "../../../../services/EmployeeMergeService";
import { success, error } from "../../../../store/CommonSlice";
import moment from "moment";
import Alert from "../../../../components/UI/ConfirmBox/Alert";

class MergeHistoryViewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      mergeHistory: null,
      employeeId: props.employeeId,
      showRollbackConfirm: false,
      rollbackMergeLogId: null,
    };
  }

  componentDidMount() {
    if (this.state.employeeId) {
      this.fetchMergeHistory();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.employeeId !== this.props.employeeId) {
      this.setState({ employeeId: this.props.employeeId }, () => {
        if (this.state.employeeId) {
          this.fetchMergeHistory();
        }
      });
    }
  }

  fetchMergeHistory = async () => {
    if (!this.state.employeeId) return;

    this.setState({ loading: true });
    try {
      const history = await EmployeeMergeService.getMergeHistory(this.state.employeeId);
      this.setState({ mergeHistory: history });
    } catch (err) {
      console.error("Error fetching merge history:", err);
      this.props.dispatch(error("Failed to load merge history"));
    } finally {
      this.setState({ loading: false });
    }
  };

  handleRollbackClick = (mergeLogId) => {
    this.setState({ showRollbackConfirm: true, rollbackMergeLogId: mergeLogId });
  };

  handleRollbackClose = () => {
    this.setState({ showRollbackConfirm: false, rollbackMergeLogId: null });
  };

  handleRollbackConfirm = async () => {
    const { rollbackMergeLogId } = this.state;
    if (!rollbackMergeLogId) return;

    this.handleRollbackClose();
    this.setState({ loading: true });
    try {
      const result = await EmployeeMergeService.rollbackMerge(null, rollbackMergeLogId);
      this.props.dispatch(success(result.message || "Merge rolled back successfully"));
      await this.fetchMergeHistory();
      if (this.props.onRollbackComplete) {
        this.props.onRollbackComplete();
      }
    } catch (err) {
      console.error("Error rolling back merge:", err);
      const errorMessage = err?.data?.error || err?.message || "Failed to rollback merge";
      this.props.dispatch(error(errorMessage));
    } finally {
      this.setState({ loading: false });
    }
  };

  canRollback = (mergeLog) => {
    if (mergeLog.status !== "completed") return false;
    const hoursSinceMerge = moment().diff(moment(mergeLog.created_at), "hours");
    return hoursSinceMerge <= 24;
  };

  getRollbackTimeLeft = (mergeLog) => {
    if (!mergeLog?.created_at || mergeLog.status !== "completed") return null;
    const expiresAt = moment(mergeLog.created_at).add(24, "hours");
    if (moment().isAfter(expiresAt)) return null;
    const d = moment.duration(expiresAt.diff(moment()));
    const h = Math.floor(d.asHours());
    const m = d.minutes();
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
  };

  getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed":
        return "merge-severity-info";
      case "rolled_back":
        return "merge-severity-warning";
      case "failed":
        return "merge-severity-critical";
      default:
        return "merge-severity-warning";
    }
  };

  renderMergeRow = (merge, type) => {
    const secondaryEmployee = (m) => m.secondaryEmployee || m.secondary_employee;
    const primaryEmployee = (m) => m.primaryEmployee || m.primary_employee;
    const mergedBy = (m) => m.mergedBy || m.merged_by;

    const employeeName = type === "merged_into"
      ? secondaryEmployee(merge)
        ? `${secondaryEmployee(merge).first_name} ${secondaryEmployee(merge).last_name}`
        : "N/A"
      : primaryEmployee(merge)
        ? `${primaryEmployee(merge).first_name} ${primaryEmployee(merge).last_name}`
        : "N/A";

    const mergedByName = mergedBy(merge)
      ? `${mergedBy(merge).first_name} ${mergedBy(merge).last_name}`
      : "N/A";

    return (
      <tr key={merge.merge_log_id}>
        <td>
          <div className="merge-refined-name">{employeeName}</div>
          {merge.merge_reason && (
            <div className="merge-refined-meta mt-1">{merge.merge_reason}</div>
          )}
        </td>
        <td className="merge-refined-meta">{mergedByName}</td>
        <td className="merge-refined-meta">{moment(merge.created_at).format("MM/DD/YYYY HH:mm")}</td>
        <td>{merge.visits_transferred || 0}</td>
        <td>
          <span className={`badge ${this.getStatusBadgeClass(merge.status)}`}>
            {merge.status.replace("_", " ")}
          </span>
        </td>
        <td>
          {this.canRollback(merge) && merge.status === "completed" && (
            <div className="d-flex flex-column align-items-start gap-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-warning"
                onClick={() => this.handleRollbackClick(merge.merge_log_id)}
                disabled={this.state.loading}
              >
                <i className="fas fa-undo me-1"></i> Rollback
              </button>
              {this.getRollbackTimeLeft(merge) && (
                <span className="merge-refined-meta small text-white" title="Time remaining to rollback">
                  {this.getRollbackTimeLeft(merge)}
                </span>
              )}
            </div>
          )}
          {merge.status === "rolled_back" && merge.updated_at && (
            <span className="merge-refined-meta small">
              {moment(merge.updated_at).format("MM/DD/YYYY")}
            </span>
          )}
          {!this.canRollback(merge) && merge.status === "completed" && (
            <span className="merge-refined-meta small">Expired (24h)</span>
          )}
        </td>
      </tr>
    );
  };

  renderMergedIntoSection = () => {
    const { mergeHistory } = this.state;
    const mergedIntoThis = mergeHistory?.merged_into_this_record || [];
    if (mergedIntoThis.length === 0) return null;
    return (
      <div className="card border-0 mb-4 p-0">
        {/* <div className="card-header sh_accordionheader merge-refined-card-head merge-refined-card-primary">
          <h6 className="mb-0">Merged Into This Record</h6>
        </div> */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <div className="merge-refined-table-wrap p-0">
              <table className="table merge-refined-table sh_tablenew mb-0">
                <thead>
                  <tr>
                    <th>Secondary Employee</th>
                    <th>Merged By</th>
                    <th>Date</th>
                    <th>Visits</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {mergedIntoThis.map((merge) => this.renderMergeRow(merge, "merged_into"))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderMergedFromSection = () => {
    const { mergeHistory } = this.state;
    const thisRecordMergedFrom = mergeHistory?.this_record_merged_from || [];
    if (thisRecordMergedFrom.length === 0) return null;
    return (
      <div className="card border-0 mb-4">
        <div className="card-header sh_accordionheader merge-refined-card-head merge-refined-card-secondary">
          <h6 className="mb-0">This Record Merged From</h6>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <div className="merge-refined-table-wrap">
              <table className="table merge-refined-table sh_tablenew mb-0">
                <thead>
                  <tr>
                    <th>Primary Employee</th>
                    <th>Merged By</th>
                    <th>Date</th>
                    <th>Visits</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {thisRecordMergedFrom.map((merge) => this.renderMergeRow(merge, "merged_from"))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { mergeHistory, loading, employeeId } = this.state;

    if (!employeeId) {
      return (
        <div className="merge-refined">
          <div className="merge-refined-alert" role="alert">
            <strong>No Employee Selected</strong>
            <p className="mb-0 mt-2 small">Please select an employee to view merge history. (0 records)</p>
          </div>
        </div>
      );
    }

    if (loading && !mergeHistory) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 merge-refined-meta">Loading merge history...</p>
        </div>
      );
    }

    const mergedIntoThis = mergeHistory?.merged_into_this_record || [];
    const thisRecordMergedFrom = mergeHistory?.this_record_merged_from || [];
    const allMerges = [...mergedIntoThis, ...thisRecordMergedFrom];

    if (allMerges.length === 0) {
      return (
        <div className="merge-refined">
          <div className="card border-0">
            <div className="card-header sh_accordionheader">
              <h6 className="mb-0">Merge History</h6>
            </div>
            <div className="card-body">
              <div className="merge-refined-alert" role="alert">
                <strong>No Merge History Found</strong>
                <p className="mb-0 mt-2">This employee has not been involved in any merge operations.</p>
                <p className="mb-0 mt-2 small">
                  Merge history appears on the <strong>primary (kept)</strong> record. If you just completed a merge, open Merge History from the primary patient's chart or refresh and try again.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="merge-refined">
        {loading && (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {this.renderMergedIntoSection()}
        {this.renderMergedFromSection()}

        {this.state.showRollbackConfirm && (
          <Alert
            type="warning"
            custonTitle="Rollback Merge"
            contentTitle="Are you sure you want to rollback this merge? This will restore the secondary employee record."
            yesButtonName="Rollback"
            noButtonName="Cancel"
            confirmButtonClassName="btn btn-warning"
            onClose={this.handleRollbackClose}
            onDelete={this.handleRollbackConfirm}
          />
        )}
      </div>
    );
  }
}

export default connect()(MergeHistoryViewer);
